import { Box, Button, Grid, HStack, Image, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { createFileRoute } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import { useState } from 'react';
import iconsPng from '@/assets/img/png/icons';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import { RIGHT_ARROW_CHARACTER } from '@/constants/stringVars';
import Dropdown from '@/components/atoms/Dropdown';

const periodOptions = ['This Month', 'Last Month', 'This Year'];

const audienceMessage =
  'Data will appear once at least 5 members from the same occupation have viewed your content, helping protect member anonymity.';

const AudienceCard = ({ title, showViewAll = false }: { title: string; showViewAll?: boolean }) => (
  <VStack
    alignItems="stretch"
    gap="14px"
    minHeight="140px"
    padding="20px"
    borderRadius="12px"
    backgroundColor="white"
    boxShadow="0 7px 20px rgba(0, 65, 121, 0.12)"
  >
    <HStack justifyContent="space-between" alignItems="center">
      <Text as="h2" variant="loraMediumTitle" fontSize="20px" lineHeight="1.25" color="text.mediumBlue">
        {title}
      </Text>
      {showViewAll && (
        <Text color="text.mediumGray" fontSize="14px" fontWeight="700">
          View All {RIGHT_ARROW_CHARACTER}
        </Text>
      )}
    </HStack>
    <HStack alignItems="center" gap="12px">
      <Image src={iconsPng.anonymous} alt="" width="26px" height="26px" flexShrink={0} />
      <Text color="text.mediumGray" fontSize="14px" lineHeight="1.4">
        {audienceMessage}
      </Text>
    </HStack>
  </VStack>
);

const EmptyMessage = ({ children }: { children: string }) => (
  <HStack justifyContent="center" gap="12px" color="text.mediumGray">
    <Info size={18} fill="#D2E1EE" color="white" aria-hidden="true" />
    <Text fontSize="14px" color="text.mediumGray">
      {children}
    </Text>
  </HStack>
);

const SectionHeading = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <HStack gap="12px" alignItems="center">
    <Text
      aria-hidden="true"
      fontSize="28px"
      lineHeight="1"
      fontWeight="700"
      background="linear-gradient(150deg, #4E1AEF, #FF8811)"
      backgroundClip="text"
      color="transparent"
    >
      ✦
    </Text>
    <VStack alignItems="flex-start" gap="0">
      <Text as="h2" variant="loraMediumTitle" fontSize="20px" lineHeight="1.25" color="text.mediumBlue">
        {title}
      </Text>
      <Text color="text.mediumGray" fontSize="12px" fontWeight="700" textTransform="uppercase">
        {subtitle}
      </Text>
    </VStack>
  </HStack>
);

const CreatorDashboard = () => {
  const [period, setPeriod] = useState('This Month');
  const periodDisclosure = useDisclosure();

  return (
    <VStack
      width="calc(100vw - 32px)"
      maxWidth={MAX_CONTAINER_WIDTH}
      marginBottom="70px"
      alignItems="stretch"
      gap="30px"
    >
      <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="16px">
        <Text
          as="h1"
          variant="loraItalicTitle"
          textAlign="left"
          fontSize="28px"
          lineHeight="36px"
          color="text.darkBlue"
        >
          Creator Dashboard
        </Text>
        <Dropdown
          disclosure={periodDisclosure}
          title="View Data From"
          options={periodOptions}
          selectedOptions={[period]}
          optionLabelDisplay={(option) => option}
          onOptionClick={(option) => {
            setPeriod(option);
            periodDisclosure.onClose();
          }}
          width={200}
          popoverWidth={200}
          trigger={
            <Button
              type="button"
              aria-label={`View data from: ${period}`}
              width="200px"
              height="50px"
              paddingX="12px"
              border="1.5px solid"
              borderColor="text.mediumBlue"
              borderRadius="8px"
              backgroundColor="white"
              boxShadow="0 5px 12px rgba(0, 65, 121, 0.12)"
              _hover={{ backgroundColor: 'white' }}
              _active={{ backgroundColor: 'white' }}
            >
              <HStack width="100%" justifyContent="space-between" alignItems="center">
                <VStack gap="0" alignItems="flex-start">
                  <Text
                    color="text.mediumGray"
                    fontSize="10px"
                    fontWeight="700"
                    textTransform="uppercase"
                    lineHeight="1.2"
                  >
                    View Data From
                  </Text>
                  <Text color="text.mediumBlue" fontSize="14px" fontWeight="700" lineHeight="1.4">
                    {period}
                  </Text>
                </VStack>
                <ChevronDownIcon
                  boxSize="4"
                  color="text.mediumGray"
                  transform={periodDisclosure.isOpen ? 'rotate(180deg)' : undefined}
                />
              </HStack>
            </Button>
          }
        />
      </HStack>

      <Grid templateColumns={{ base: '1fr', lg: 'minmax(270px, 1fr) minmax(0, 2.05fr)' }} gap="20px" alignItems="start">
        <VStack alignItems="stretch" gap="20px">
          <AudienceCard title="Age Distribution" />
          <AudienceCard title="Audience by Occupation" showViewAll />
        </VStack>

        <VStack alignItems="stretch" gap="20px">
          <VStack
            alignItems="stretch"
            minHeight="360px"
            padding="20px"
            borderRadius="12px"
            backgroundColor="white"
            boxShadow="0 7px 20px rgba(0, 65, 121, 0.12)"
          >
            <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="24px">
              <SectionHeading title="Summary" subtitle="Published Items 0" />
              <HStack alignItems="center" gap="24px">
                <HStack alignItems="center" gap="12px">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="66px"
                    height="48px"
                    borderRadius="9px"
                    backgroundColor="#EDE8FD"
                    color="primary.500"
                  >
                    <Image src={iconsPng.creatorDashboardViewsEye} alt="" width="20px" height="20px" />
                  </Box>
                  <VStack gap="0" minWidth="64px" alignItems="center">
                    <Text
                      color="text.darkBlue"
                      fontFamily="Lora"
                      fontSize="22px"
                      fontStyle="italic"
                      lineHeight={'22px'}
                      fontWeight="700"
                      paddingTop={'4px'}
                    >
                      0
                    </Text>
                    <Text color="text.mediumGray" fontSize="12px" lineHeight={'18px'} fontWeight="500">
                      Views
                    </Text>
                  </VStack>
                </HStack>
                <HStack alignItems="center" gap="12px">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    width="66px"
                    height="48px"
                    borderRadius="9px"
                    backgroundColor="#FFF1E4"
                    color="secondary.500"
                  >
                    <Image src={iconsPng.engagementOrange} alt="" width="20px" height="20px" />
                  </Box>
                  <VStack gap="0" minWidth="88px" justifyContent="center">
                    <Text
                      color="text.darkBlue"
                      fontFamily="Lora"
                      fontSize="22px"
                      fontStyle="italic"
                      lineHeight={'22px'}
                      fontWeight="700"
                      paddingTop={'4px'}
                    >
                      0%
                    </Text>
                    <Text color="text.mediumGray" fontSize="12px" lineHeight={'18px'} fontWeight="500">
                      Engagement
                    </Text>
                  </VStack>
                </HStack>
              </HStack>
            </HStack>

            <VStack gap="12px" marginTop="20px">
              <Text as="h3" color="text.mediumBlue" fontSize="16px" fontWeight="700">
                Publishing Activity
              </Text>
              <EmptyMessage>No content was published during this date range.</EmptyMessage>
            </VStack>

            <HStack justifyContent="space-around" alignItems="flex-end" flex="1" paddingTop="26px">
              {['1st', '2nd', '3rd', '4th', '5th'].map((week) => (
                <VStack key={week} gap="9px">
                  <Box width="32px" height="10px" borderRadius="full" backgroundColor="text.mediumGray" />
                  <Text color="text.mediumGray" fontSize="12px" fontWeight="700">
                    {week} Week{' '}
                    <Text as="span" color="text.darkBlue" fontWeight="400">
                      0
                    </Text>
                  </Text>
                </VStack>
              ))}
            </HStack>
          </VStack>

          <VStack
            alignItems="stretch"
            gap="12px"
            padding="20px"
            borderRadius="12px"
            backgroundColor="white"
            boxShadow="0 7px 20px rgba(0, 65, 121, 0.12)"
          >
            <HStack justifyContent="space-between" alignItems="center">
              <SectionHeading title="Top Performers" subtitle="Top 4" />
              <Text color="text.mediumGray" fontSize="14px" fontWeight="700">
                View All {RIGHT_ARROW_CHARACTER}
              </Text>
            </HStack>
            <EmptyMessage>No top-performing content for the selected date range.</EmptyMessage>
          </VStack>
        </VStack>
      </Grid>
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_creator/creator/dashboard')({
  staticData: { title: 'Creator Dashboard' },
  component: CreatorDashboard,
});
